import apiClient from '../client'
import type { InboundNumber, PaginatedResponse } from '@/types'

// Transform backend response to frontend PaginatedResponse format
const transformInboundResponse = (
  data: { inbound_numbers: InboundNumber[]; total: number }
): PaginatedResponse<InboundNumber> => ({
  items: data.inbound_numbers || [],
  total: data.total || 0,
  page: 1,
  page_size: data.total || 25,
  total_pages: 1,
})

export interface TransferConfig {
  default?: string
  pricing?: string
  technical?: string
}

export interface CreateInboundNumberData {
  phone_number: string
  name: string
  description?: string
  is_active?: boolean
  is_primary?: boolean
  voice?: string
  model?: string
  language?: string
  max_duration?: number
  prompt_type?: string
  transfer_config?: TransferConfig
  enable_advisor_tool?: boolean
  enable_caller_context_tool?: boolean
  enable_email_tool?: boolean
  email_cc?: string
  email_bcc?: string
  transfer_notify_email?: string
  business_hours_start?: number
  business_hours_end?: number
  business_hours_timezone?: string
}

export interface UpdateInboundNumberData {
  name?: string
  description?: string
  is_active?: boolean
  is_primary?: boolean
  voice?: string
  model?: string
  language?: string
  max_duration?: number
  prompt_type?: string
  transfer_config?: TransferConfig
  enable_advisor_tool?: boolean
  enable_caller_context_tool?: boolean
  enable_email_tool?: boolean
  email_cc?: string
  email_bcc?: string
  transfer_notify_email?: string
  business_hours_start?: number
  business_hours_end?: number
  business_hours_timezone?: string
}

export const inboundApi = {
  getAll: async (activeOnly?: boolean) => {
    const response = await apiClient.get<{ inbound_numbers: InboundNumber[]; total: number }>('/api/inbound', {
      params: activeOnly ? { active_only: true } : undefined
    })

    return {
      ...response,
      data: transformInboundResponse(response.data),
    }
  },

  getById: (phoneNumber: string) =>
    apiClient.get<InboundNumber>(`/api/inbound/${encodeURIComponent(phoneNumber)}`),

  getPrimary: () =>
    apiClient.get<InboundNumber>('/api/inbound/primary'),

  create: (data: CreateInboundNumberData) =>
    apiClient.post<{ success: boolean; config: InboundNumber }>('/api/inbound', {
      phone_number: data.phone_number,
      name: data.name,
      description: data.description,
      is_primary: data.is_primary,
      use_advisor_tool: data.enable_advisor_tool,
      use_caller_context_tool: data.enable_caller_context_tool,
      use_email_tool: data.enable_email_tool,
      voice: data.voice,
      model: data.model,
      language: data.language,
      max_duration: data.max_duration,
      transfer_config: data.transfer_config,
      prompt_type: data.prompt_type,
    }),

  update: (phoneNumber: string, data: UpdateInboundNumberData) =>
    apiClient.put<{ success: boolean; config: InboundNumber }>(`/api/inbound/${encodeURIComponent(phoneNumber)}`, {
      name: data.name,
      description: data.description,
      is_active: data.is_active,
      is_primary: data.is_primary,
      use_advisor_tool: data.enable_advisor_tool,
      use_caller_context_tool: data.enable_caller_context_tool,
      use_email_tool: data.enable_email_tool,
      voice: data.voice,
      model: data.model,
      language: data.language,
      max_duration: data.max_duration,
      transfer_config: data.transfer_config,
      prompt_type: data.prompt_type,
      email_cc: data.email_cc,
      email_bcc: data.email_bcc,
      transfer_notify_email: data.transfer_notify_email,
    }),

  delete: (phoneNumber: string) =>
    apiClient.delete(`/api/inbound/${encodeURIComponent(phoneNumber)}`),

  setPrimary: (phoneNumber: string) =>
    apiClient.put<{ success: boolean; config: InboundNumber }>(`/api/inbound/${encodeURIComponent(phoneNumber)}`, {
      is_primary: true
    }),

  syncWithBland: (phoneNumber: string) =>
    apiClient.post<{ success: boolean; result: unknown }>('/api/inbound/configure', {
      phone_number: phoneNumber
    }),

  configure: (phoneNumber?: string) =>
    apiClient.post<{ success: boolean; result: unknown }>('/api/inbound/configure',
      phoneNumber ? { phone_number: phoneNumber } : {}
    ),

  getBlandStatus: (phoneNumber: string) =>
    apiClient.get<{ success: boolean; bland_config: unknown }>(`/api/inbound/${encodeURIComponent(phoneNumber)}/bland-status`),

  listBlandTools: () =>
    apiClient.get<{ success: boolean; tools: unknown[]; count: number }>('/api/inbound/tools/list'),

  // Voices
  listVoices: () =>
    apiClient.get<{
      voices: Array<{
        voice_id?: string
        id?: string
        name?: string
        description?: string
        gender?: string
        language?: string
        [key: string]: unknown
      }>
      total: number
      default_voice_id: string
    }>('/api/inbound/voices'),

  // Call Settings
  getCallSettings: () =>
    apiClient.get<{
      outbound_caller_id: string
      outbound_voice: string
      inbound_number: string
    }>('/api/inbound/settings/call'),

  updateCallSettings: (settings: { outbound_caller_id?: string; outbound_voice?: string }) =>
    apiClient.put<{
      success: boolean
      message: string
      outbound_caller_id: string
      outbound_voice: string
      effective_caller_id: string
    }>('/api/inbound/settings/call', settings),

  // Configuration actions
  configureAll: () =>
    apiClient.post<{ success: boolean; result: unknown }>('/api/inbound/configure', {}),

  configureLegacy: () =>
    apiClient.post<{ success: boolean; result: unknown }>('/api/inbound/configure/legacy', {}),

  seedDefault: () =>
    apiClient.post<{ success: boolean; message: string; config?: unknown }>('/api/inbound/seed-default', {}),
}
